import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { GripVertical, X } from "lucide-react";

/** Props for the SortableOptionRow component. */
export interface SortableOptionRowProps {
  id: string;
  value: string;
  index: number;
  isDuplicate: boolean;
  disabled: boolean;
  onValueChange: (value: string) => void;
  onRemove: () => void;
}

/**
 * A single draggable option row within the SelectOptionsEditor.
 * @param id - Unique sortable identifier
 * @param value - The option text value
 * @param index - Zero-based position in the list
 * @param isDuplicate - Whether this option duplicates another
 * @param disabled - Whether editing is disabled
 * @param onValueChange - Callback when the text changes
 * @param onRemove - Callback to remove this option
 */
export function SortableOptionRow({
  id,
  value,
  index,
  isDuplicate,
  disabled,
  onValueChange,
  onRemove,
}: SortableOptionRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: isDragging ? 0.5 : 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="flex items-center gap-1.5"
    >
      <div
        className={`shrink-0 ${disabled ? "cursor-default" : "cursor-grab active:cursor-grabbing"} touch-manipulation`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50" />
      </div>
      <span className="shrink-0 w-5 text-center text-[10px] tabular-nums text-muted-foreground/50 select-none">
        {index + 1}
      </span>
      <Input
        placeholder={`Option ${index + 1}`}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={disabled}
        className={`flex-1 h-7 text-xs ${isDuplicate ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
      />
      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={disabled}
        className="shrink-0 h-6 w-6 text-muted-foreground hover:text-destructive transition-colors"
      >
        <X className="h-3 w-3" />
      </Button>
    </motion.div>
  );
}
